/* eslint max-lines: ["error", { "max": 532 }] */
import {
  IconBuilding,
  IconCheck,
  IconGavel,
  IconWritingSign,
  IconX,
} from '@tabler/icons-react';
import { Checkbox } from '@zorro/design-system/v2/Checkbox';
import { Divider } from '@zorro/design-system/v2/Divider';
import { NumberInput } from '@zorro/design-system/v2/NumberInput';
import { RadioCard, RadioGroup } from '@zorro/design-system/v2/Radio';
import { Select } from '@zorro/design-system/v2/Select';
import { TextInput } from '@zorro/design-system/v2/TextInput';
import { ContactIcon, IconPersonPurple } from '@zorro/shared/assets';
import { capitalizeStartOfSentence } from '@zorro/shared/formatters';
import { YesNo } from '@zorro/types';
import {
  AddressInput,
  DateInput,
  FormErrorMessage,
  Grid,
  Icon,
  LabelWithTooltip,
  Space,
  Stack,
} from '@zorro/web/design-system';
import { downloadEmployerDocument, useRoles } from '@zorro/web/ui-utils';
import { Dispatch, SetStateAction } from 'react';
import {
  Control,
  Controller,
  UseFormReturn,
  useFieldArray,
} from 'react-hook-form';

import { IconWithTitle } from '../EnrollmentSummary';
import { FileUploader } from '../FileUploader';
import { PersonFormArray, getDefaultPersonFormItem } from '../PersonFormArray';
import { TaxIdInput } from '../TaxIdInput';
import { ReturnType, UploadLogoInput } from '../UploadLogoInput';
import { UploadedFileBox } from '../UploadedFileBox';
import { BusinessEntityInput } from './BusinessEntityInput';
import {
  CompanyDetailsFormFields,
  hasBeneficialOwnersDescriptions,
  stateSelectOptions,
} from './CompanyDetailsUtils';
import { NaicsCodeInput } from './NaicsCodeInput';

type Props = {
  companyDetailsForm: UseFormReturn<CompanyDetailsFormFields>;
  isSignedAgreementRequired?: boolean;
  isFinalizationMode?: boolean;
  setHasLogoBeenDeleted: Dispatch<SetStateAction<boolean>>;
  hasLogoBeenDeleted: boolean;
  isSignedAgreementVisible: boolean;
  isNaicsCodeVisible?: boolean;
  isLynxPaymentsEnabled?: boolean;
};

export const CompanyDetailsFormInputs = ({
  companyDetailsForm,
  isSignedAgreementRequired = false,
  isSignedAgreementVisible = false,
  isFinalizationMode = false,
  isNaicsCodeVisible = false,
  setHasLogoBeenDeleted,
  hasLogoBeenDeleted,
  isLynxPaymentsEnabled = false,
}: Props) => {
  'use no memo';
  const { isAgentWithoutSupervisor: isReadonlyMode } = useRoles();
  const { watch, control, trigger, setValue, formState } = companyDetailsForm;
  const { errors } = formState;

  const signedAgreement = watch('signedAgreement');
  const isLegalNameSameAsCompanyName = watch('isLegalNameSameAsCompanyName');
  const isMailingAddressSameAsHQAddress = watch(
    'isMailingAddressSameAsHQAddress'
  );
  const hasBeneficialOwners = watch('hasBeneficialOwners');

  const {
    fields: beneficialOwnerFields,
    append: appendBeneficialOwner,
    remove: removeBeneficialOwner,
  } = useFieldArray({ control, name: 'beneficialOwners' });

  return (
    <Stack gap="3.5rem">
      <Stack gap="xl">
        <IconWithTitle title="General information" icon={<IconBuilding />} />
        <Grid>
          <Grid.Col>
            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <TextInput
                  {...field}
                  isReadOnly={isReadonlyMode}
                  placeholder="Type company name"
                  label={
                    <LabelWithTooltip
                      label="Company name (DBA)"
                      tooltipLabel="The name under which the company is doing business"
                      isRequired
                    />
                  }
                />
              )}
            />

            <FormErrorMessage fieldName="name" errors={errors} />
          </Grid.Col>
          <Grid.Col>
            <Controller
              control={control}
              name="isLegalNameSameAsCompanyName"
              render={({ field: { value, ...rest } }) => (
                <Checkbox
                  {...rest}
                  checked={value}
                  isReadOnly={isReadonlyMode}
                  label="Legal name is the same as company name (DBA)"
                />
              )}
            />

            <FormErrorMessage
              fieldName="isLegalNameSameAsCompanyName"
              errors={errors}
            />
          </Grid.Col>
          {!isLegalNameSameAsCompanyName && (
            <Grid.Col>
              <Controller
                control={control}
                name="legalName"
                render={({ field }) => (
                  <TextInput
                    {...field}
                    disabled={isReadonlyMode}
                    placeholder="Type legal name"
                    required={isFinalizationMode}
                    label="Legal name"
                  />
                )}
              />

              <FormErrorMessage fieldName="legalName" errors={errors} />
            </Grid.Col>
          )}
          {isLynxPaymentsEnabled && (
            <Grid.Col>
              <Space h="lg" />
              <Controller
                control={control}
                name="websiteUrl"
                render={({ field }) => (
                  <TextInput
                    {...field}
                    isReadOnly={isReadonlyMode}
                    readOnlyVariant="url"
                    placeholder="https://www.company.com"
                    label="Website URL"
                  />
                )}
              />

              <FormErrorMessage fieldName="websiteUrl" errors={errors} />
            </Grid.Col>
          )}
          <Grid.Col>
            <Space h="lg" />
            <Controller
              control={control}
              name="address"
              render={({ field: { onChange, ...rest } }) => (
                <AddressInput
                  {...rest}
                  isReadOnly={isReadonlyMode}
                  required={isFinalizationMode}
                  notifySubscribers={(address: string) => onChange(address)}
                  onChange={onChange}
                  placeholder="Type the HQ address"
                  label="HQ address"
                  size="md"
                />
              )}
            />

            <FormErrorMessage fieldName="address" errors={errors} />
          </Grid.Col>
          <Grid.Col>
            <Controller
              control={control}
              name="isMailingAddressSameAsHQAddress"
              render={({ field: { value, ...rest } }) => (
                <Checkbox
                  {...rest}
                  checked={value}
                  isReadOnly={isReadonlyMode}
                  label="Mailing address is the same as HQ address"
                />
              )}
            />

            <FormErrorMessage
              fieldName="isMailingAddressSameAsHQAddress"
              errors={errors}
            />
          </Grid.Col>
          {!isMailingAddressSameAsHQAddress && (
            <Grid.Col>
              <Controller
                control={control}
                name="mailingAddress"
                render={({ field: { onChange, ...rest } }) => (
                  <AddressInput
                    {...rest}
                    required={isFinalizationMode}
                    disabled={isReadonlyMode}
                    notifySubscribers={(address: string) => onChange(address)}
                    onChange={onChange}
                    placeholder="Type the Mailing address"
                    label="Mailing address"
                    size="md"
                  />
                )}
              />

              <FormErrorMessage fieldName="mailingAddress" errors={errors} />
            </Grid.Col>
          )}
          <Grid.Col>
            <Space h="lg" />
            <UploadLogoInput
              isRequired={isFinalizationMode}
              isDisabled={isReadonlyMode}
              isReadonly={isReadonlyMode}
              onDeleteCurrentLogo={() => {
                setValue('logo', undefined, { shouldValidate: true });
                setHasLogoBeenDeleted(true);
              }}
              onLogoUploaded={(logo) => {
                setValue('logo', logo, { shouldValidate: true });
                setHasLogoBeenDeleted(false);
              }}
              control={control}
              watch={watch}
              returnType={ReturnType.FILE}
              hasLogoBeenDeleted={hasLogoBeenDeleted}
            />
            <FormErrorMessage fieldName="logo" errors={errors} />
          </Grid.Col>
        </Grid>
      </Stack>

      <Divider />

      <Stack gap="xl">
        <IconWithTitle title="Legal & tax information" icon={<IconGavel />} />
        <Grid>
          <Grid.Col span={6}>
            <Controller
              name="businessEntityType"
              control={control}
              render={({ field }) => (
                <BusinessEntityInput
                  {...field}
                  selectProps={{
                    required: isFinalizationMode,
                    isReadOnly: isReadonlyMode,
                  }}
                />
              )}
            />
            <FormErrorMessage fieldName="businessEntityType" errors={errors} />
          </Grid.Col>
          <Grid.Col span={6}>
            <TaxIdInput
              control={control}
              trigger={trigger}
              errors={errors}
              watch={watch}
              isReadOnly={isReadonlyMode}
              isRequired={isFinalizationMode}
            />
          </Grid.Col>
          {isLynxPaymentsEnabled && (
            <>
              <Grid.Col span={6}>
                <Space h="lg" />
                <Controller
                  name="stateOfIncorporation"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label="State of incorporation"
                      placeholder="Select US state"
                      data={stateSelectOptions}
                      searchable
                      required={isFinalizationMode}
                      isReadOnly={isReadonlyMode}
                      size="md"
                    />
                  )}
                />
                <FormErrorMessage
                  fieldName="stateOfIncorporation"
                  errors={errors}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <Space h="lg" />
                <Controller
                  name="yearsInBusiness"
                  control={control}
                  render={({ field: { value, onChange, ...rest } }) => (
                    <NumberInput
                      {...rest}
                      value={value ?? undefined}
                      onChange={(val) => onChange(val === '' ? undefined : val)}
                      label="Years in business"
                      placeholder="Type number of years"
                      required={isFinalizationMode}
                      isReadOnly={isReadonlyMode}
                      size="md"
                    />
                  )}
                />
                <FormErrorMessage fieldName="yearsInBusiness" errors={errors} />
              </Grid.Col>
            </>
          )}

          {isNaicsCodeVisible && (
            <Grid.Col span={12}>
              <Space h="lg" />
              <Controller
                name="naicsCode"
                control={control}
                render={({ field }) => (
                  <NaicsCodeInput
                    {...field}
                    isFinalizationMode={isFinalizationMode}
                    isReadonlyMode={isReadonlyMode}
                  />
                )}
              />
              <FormErrorMessage fieldName="naicsCode" errors={errors} />
            </Grid.Col>
          )}
        </Grid>
      </Stack>

      {isLynxPaymentsEnabled && (
        <>
          <Divider />
          <Stack gap="xl">
            <Controller
              control={control}
              name="hasBeneficialOwners"
              render={({ field: { onChange, ...rest } }) => (
                <RadioGroup
                  {...rest}
                  onChange={(val) => {
                    onChange(val);
                    setValue('beneficialOwners', [
                      getDefaultPersonFormItem(false, true),
                    ]);
                  }}
                  label="Does any individual own 25% or more of the business?"
                  isReadOnly={isReadonlyMode}
                  required={isFinalizationMode}
                >
                  <Grid mt="sm">
                    {(!isReadonlyMode || rest.value === YesNo.YES) && (
                      <Grid.Col span={6}>
                        <RadioCard
                          icon={<Icon icon={IconCheck} />}
                          iconColor="green"
                          label="Yes"
                          description={capitalizeStartOfSentence(
                            hasBeneficialOwnersDescriptions[YesNo.YES]
                          )}
                          value={YesNo.YES}
                        />
                      </Grid.Col>
                    )}
                    {(!isReadonlyMode || rest.value === YesNo.NO) && (
                      <Grid.Col span={6}>
                        <RadioCard
                          icon={<Icon icon={IconX} />}
                          iconColor="red"
                          label="No"
                          description={capitalizeStartOfSentence(
                            hasBeneficialOwnersDescriptions[YesNo.NO]
                          )}
                          value={YesNo.NO}
                        />
                      </Grid.Col>
                    )}
                  </Grid>
                </RadioGroup>
              )}
            />
            <FormErrorMessage fieldName="hasBeneficialOwners" errors={errors} />
            {hasBeneficialOwners !== undefined && (
              <PersonFormArray
                icon={
                  hasBeneficialOwners === YesNo.YES ? (
                    <ContactIcon />
                  ) : (
                    <IconPersonPurple />
                  )
                }
                title={
                  hasBeneficialOwners === YesNo.YES
                    ? 'Beneficial owners'
                    : 'Officer'
                }
                subtitle={
                  hasBeneficialOwners === YesNo.YES
                    ? 'People with at least 25% ownership in the business'
                    : 'A person with significant control or management responsibility'
                }
                personTitle={
                  hasBeneficialOwners === YesNo.YES
                    ? 'Beneficial owner'
                    : undefined
                }
                control={control}
                errors={errors}
                fieldName="beneficialOwners"
                activeName="beneficialOwners"
                fields={beneficialOwnerFields}
                append={appendBeneficialOwner}
                remove={removeBeneficialOwner}
                isFinalizationMode={isFinalizationMode}
                shouldShowIdentityFields
                shouldHideMakeAdmin
                isMultiSelect={hasBeneficialOwners === YesNo.YES}
                maxItems={hasBeneficialOwners === YesNo.YES ? 4 : undefined}
              />
            )}
          </Stack>
        </>
      )}

      {isSignedAgreementVisible && (
        <>
          <Divider />
          <Stack gap="xl">
            <IconWithTitle
              title="Zorro service agreement"
              icon={<IconWritingSign />}
            />
            <Grid>
              <Grid.Col>
                {signedAgreement ? (
                  <UploadedFileBox
                    label="Signed agreement"
                    name={`File: ${
                      signedAgreement instanceof File
                        ? signedAgreement.name
                        : signedAgreement.fileName
                    }`}
                    onClickDelete={() => setValue('signedAgreement', undefined)}
                    onClickDownload={() => {
                      if (!(signedAgreement instanceof File)) {
                        // eslint-disable-next-line @typescript-eslint/no-floating-promises
                        downloadEmployerDocument(
                          signedAgreement.documentUrl,
                          signedAgreement.fileName
                        );
                      }
                    }}
                    isReadonly={isReadonlyMode}
                    isRequired={isSignedAgreementRequired}
                  />
                ) : (
                  <FileUploader
                    control={control as unknown as Control}
                    name="signedAgreement"
                    label="Signed agreement"
                    isLoading={false}
                    onDrop={(files: File[], onBlur, onChange) => {
                      onChange(files[0]);
                      onBlur();
                    }}
                    isDisabled={isReadonlyMode}
                    buttonText="Upload signed agreement"
                    zoneLabelText="Or drag it here"
                    isRequired={isSignedAgreementRequired}
                  />
                )}
                <FormErrorMessage fieldName="signedAgreement" errors={errors} />
              </Grid.Col>
              <Grid.Col span={6}>
                <Space h="lg" />
                <Controller
                  control={control}
                  name="agreementSignedDate"
                  render={({ field }) => (
                    <DateInput
                      {...field}
                      label="Date agreement signed"
                      required={isSignedAgreementRequired}
                      placeholder="Select date"
                      size="md"
                    />
                  )}
                />
                <FormErrorMessage
                  fieldName="agreementSignedDate"
                  errors={errors}
                />
              </Grid.Col>
              <Grid.Col span={6} />
            </Grid>
          </Stack>
        </>
      )}
    </Stack>
  );
};
